// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — SwimlaneRenderer (M3: V·IANet Flow)
// Renders phase header bar, vertical dashed dividers, and
// role footer bar behind the flowchart canvas.
// Canvas: 1500×880 px (panoramic)
// ═══════════════════════════════════════════════════════════════

import { useState, useRef, useEffect } from 'react';
import { FLOW_TOKENS as T } from './flowTokens';
import type { FlowPhase } from '../types/flowchart.types';

// ─── Lane Type ───────────────────────────────────────────────────────────────

export interface SwimlaneRenderLane {
  id: string;
  name: string;
  y: number;
  height: number;
}

// ─── Role Type ───────────────────────────────────────────────────────────────

export interface FlowRole {
  id: string;
  name: string;
  phaseId: string;
  department?: string;
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface SwimlaneRendererProps {
  lanes: SwimlaneRenderLane[];
  phases?: FlowPhase[];
  roles?: FlowRole[];
  onRenamePhase?: (id: string, name: string) => void;
  onRemovePhase?: (id: string) => void;
  readOnly?: boolean;
}

// ─── Layout Constants ────────────────────────────────────────────────────────

const PHASE_HEADER_H = 30;
const CW = T.canvas.a3Width; // 1500
const CH = T.canvas.a3Height; // 780
const BODY_TOP = PHASE_HEADER_H;
const BODY_BOTTOM = CH;

// ─── Editable Phase Header ──────────────────────────────────────────────────

function PhaseHeader({
  phase,
  onRename,
  onRemove,
  readOnly,
  phaseIndex: _phaseIndex,
}: {
  phase: { id: string; name: string; x: number; width: number };
  onRename?: (id: string, name: string) => void;
  onRemove?: (id: string) => void;
  readOnly?: boolean;
  phaseIndex?: number;
}) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(phase.name);
  const [hovered, setHovered] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleSave = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== phase.name) {
      onRename?.(phase.id, trimmed);
    } else {
      setEditValue(phase.name);
    }
    setEditing(false);
  };

  return (
    <div
      style={{
        position: 'absolute',
        left: phase.x,
        top: 0,
        width: phase.width,
        height: PHASE_HEADER_H,
        backgroundColor: readOnly ? 'rgba(47,84,150,0.12)' : '#D8D8D8',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRight: readOnly ? 'none' : '1px solid #BFBFBF',
        borderBottom: readOnly ? '1px solid rgba(31,56,100,0.06)' : '1px solid #BFBFBF',
        borderRadius: readOnly ? 8 : 0,
        boxSizing: 'border-box',
        pointerEvents: readOnly ? 'none' : 'auto',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onDoubleClick={() => {
        if (!readOnly && onRename) {
          setEditValue(phase.name);
          setEditing(true);
        }
      }}
    >
      {editing ? (
        <input
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave();
            if (e.key === 'Escape') {
              setEditValue(phase.name);
              setEditing(false);
            }
          }}
          style={{
            width: '90%',
            fontSize: '10px',
            fontWeight: 700,
            fontFamily: T.font.family,
            color: '#333333',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            textAlign: 'center',
            border: '1px solid #33BCE1',
            borderRadius: 2,
            padding: '1px 4px',
            outline: 'none',
            background: '#fff',
          }}
        />
      ) : (
        <span
          style={{
            fontSize: '10px',
            fontWeight: 700,
            fontFamily: T.font.family,
            color: '#333333',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            lineHeight: 1,
          }}
        >
          {phase.name}
        </span>
      )}

      {/* Delete button on hover */}
      {hovered && !readOnly && !editing && onRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove(phase.id);
          }}
          style={{
            position: 'absolute',
            right: 2,
            top: 2,
            width: 14,
            height: 14,
            borderRadius: '50%',
            border: 'none',
            background: 'rgba(220,38,38,0.8)',
            color: '#fff',
            fontSize: '9px',
            fontWeight: 700,
            lineHeight: 1,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
          }}
          title="Eliminar fase"
        >
          ×
        </button>
      )}
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function SwimlaneRenderer({
  lanes,
  phases,
  roles: _roles,
  onRenamePhase,
  onRemovePhase,
  readOnly,
}: SwimlaneRendererProps) {
  const hasPhases = phases && phases.length > 0;

  if (!hasPhases) return null;

  // Compute phase x offsets
  let phaseOffsets: { id: string; name: string; x: number; width: number }[] = [];
  if (hasPhases) {
    let currentX = 0;
    phaseOffsets = phases!.map((p) => {
      const entry = { id: p.id, name: p.name, x: currentX, width: p.width };
      currentX += p.width;
      return entry;
    });
  }

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 0,
        overflow: 'visible',
      }}
    >
      {/* ── Top bar: phase columns (y=0, h=30) ── */}
      {phaseOffsets.map((phase, idx) => (
        <PhaseHeader
          key={`phase-header-${phase.id}`}
          phase={phase}
          onRename={onRenamePhase}
          onRemove={onRemovePhase}
          readOnly={readOnly}
          phaseIndex={idx}
        />
      ))}

      {/* ── Vertical dashed lines at phase boundaries ── */}
      {phaseOffsets.slice(0, -1).map((phase) => (
        <div
          key={`phase-line-${phase.id}`}
          style={{
            position: 'absolute',
            left: phase.x + phase.width,
            top: BODY_TOP,
            width: 0,
            height: BODY_BOTTOM - BODY_TOP,
            borderLeft: '0.5px dashed #D8D8D8',
          }}
        />
      ))}

      {/* Role footer removed — canvas uses full height */}

      {/* ── Swimlane bands (legacy support) ── */}
      {lanes &&
        lanes.length > 0 &&
        lanes.map((lane, index) => (
          <div
            key={lane.id}
            style={{
              position: 'absolute',
              left: 0,
              top: lane.y + PHASE_HEADER_H,
              height: lane.height,
              width: CW,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              style={{
                height: T.swimlane.headerHeight,
                backgroundColor: T.swimlane.headerFill,
                color: T.swimlane.headerText,
                fontSize: T.font.laneHeader.size,
                fontWeight: T.font.laneHeader.weight,
                fontFamily: T.font.family,
                letterSpacing: T.font.laneHeader.letterSpacing,
                textTransform: 'uppercase',
                display: 'flex',
                alignItems: 'center',
                paddingLeft: 8,
                flexShrink: 0,
              }}
            >
              {lane.name}
            </div>
            <div
              style={{
                flex: 1,
                backgroundColor: index % 2 === 0 ? T.color.surface : T.swimlane.altBackground,
              }}
            />
            <div
              style={{
                height: T.swimlane.dividerWidth,
                backgroundColor: T.swimlane.dividerColor,
                flexShrink: 0,
              }}
            />
          </div>
        ))}
    </div>
  );
}
